import { useRef, useState } from "react";
import { Upload, Trash2, ImageIcon, Loader2, Printer, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";

const MAX_BYTES = 500_000;
const REQUIRED_WIDTH = 570;
const REQUIRED_HEIGHT = 300;

/** Parse BMP header in the browser (no external deps) and validate
 * that the file is exactly 1-bit monochrome 570×300 and ≤500 KB. */
async function readAndValidateBmp(file) {
  if (file.size > MAX_BYTES) {
    throw new Error(`Το αρχείο είναι ${Math.round(file.size / 1024)} KB. Όριο 500 KB.`);
  }
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  if (bytes.length < 54 || bytes[0] !== 0x42 || bytes[1] !== 0x4D) {
    throw new Error("Δεν είναι έγκυρο αρχείο BMP (λείπει υπογραφή «BM»).");
  }
  const dv = new DataView(buf);
  const width = dv.getInt32(18, true);
  const height = Math.abs(dv.getInt32(22, true));
  const bpp = dv.getUint16(28, true);
  if (width !== REQUIRED_WIDTH) {
    throw new Error(`Πλάτος ${width}px — απαιτείται ${REQUIRED_WIDTH}px.`);
  }
  if (height !== REQUIRED_HEIGHT) {
    throw new Error(`Ύψος ${height}px — απαιτείται ${REQUIRED_HEIGHT}px.`);
  }
  if (bpp !== 1) {
    throw new Error(`Bit depth ${bpp}-bit — απαιτείται 1-bit Monochrome.`);
  }
  // Build data URL
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return `data:image/bmp;base64,${btoa(bin)}`;
}

const SPECS = [
  { label: "Format", value: "BMP", icon: CheckCircle2 },
  { label: "Διαστάσεις", value: "570 × 300 px", icon: CheckCircle2 },
  { label: "Bit depth", value: "1-bit Monochrome (Black/White)", icon: CheckCircle2 },
  { label: "Μέγιστο μέγεθος", value: "500 KB", icon: CheckCircle2 },
];

export default function BusinessLogoUploader({
  value,
  printEnabled,
  printProformaEnabled,
  onChange,
  onPrintToggle,
  onPrintProformaToggle,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setLastError("");
    setBusy(true);
    try {
      const dataUrl = await readAndValidateBmp(file);
      const r = await http.put("/settings/business-logo", { logo: dataUrl });
      onChange?.(r.data?.business_logo ?? dataUrl);
      toast.success("Το logo αποθηκεύτηκε");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Αποτυχία ανεβάσματος logo";
      setLastError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
      // reset file input so the same file can be re-selected after fixing
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clearLogo = async () => {
    if (!(await confirmDialog("Αφαίρεση του logo;"))) return;
    setBusy(true);
    try {
      await http.put("/settings/business-logo", { logo: "" });
      onChange?.("");
      toast.success("Το logo αφαιρέθηκε");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  const togglePrint = async (next) => {
    try {
      await http.put("/settings/business-logo", { print_enabled: next });
      onPrintToggle?.(next);
      toast.success(next ? "Εκτύπωση logo (Απόδειξη): Ενεργοποιημένη" : "Εκτύπωση logo (Απόδειξη): Απενεργοποιημένη");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    }
  };

  const togglePrintProforma = async (next) => {
    try {
      await http.put("/settings/business-logo", { print_proforma_enabled: next });
      onPrintProformaToggle?.(next);
      toast.success(next ? "Εκτύπωση logo (Λογαριασμός/Delivery): Ενεργοποιημένη" : "Εκτύπωση logo (Λογαριασμός/Delivery): Απενεργοποιημένη");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    }
  };

  return (
    <div>
      <h3 className="font-heading text-lg font-semibold mb-1">Logo Επιχείρησης</h3>
      <p className="text-xs text-slate-500 mb-4">
        Το logo χρησιμοποιείται στις εκτυπώσεις αποδείξεων και στην πλευρική μπάρα.
        Πρέπει να έχει συγκεκριμένο format για την ταμειακή.
      </p>

      {/* Spec sheet — what the file must look like */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-600 font-bold mb-2">
          Προδιαγραφές Αρχείου
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
          {SPECS.map(({ label, value, icon: Ico }) => (
            <div key={label} className="flex items-center gap-2">
              <Ico size={14} className="text-emerald-600 shrink-0" />
              <span className="text-slate-600">{label}:</span>
              <span className="font-mono font-bold text-slate-800">{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-start gap-5 mb-4">
        <div
          className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 grid place-items-center overflow-hidden shrink-0"
          style={{ width: 190, height: 100 }}
        >
          {value ? (
            <img
              src={value}
              alt="Logo Επιχείρησης"
              className="object-contain"
              style={{ width: 190, height: 100 }}
              data-testid="business-logo-preview"
            />
          ) : (
            <div className="flex flex-col items-center text-slate-400">
              <ImageIcon size={26} />
              <div className="text-[10px] mt-1 uppercase tracking-wider font-bold">Κανένα</div>
            </div>
          )}
        </div>

        <div className="flex-1 space-y-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/bmp,.bmp"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="business-logo-input"
          />
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              data-testid="business-logo-upload-btn"
              className="h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm flex items-center gap-2 disabled:opacity-40"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {value ? "Αλλαγή BMP" : "Ανέβασμα BMP"}
            </button>
            {value && (
              <button
                type="button"
                disabled={busy}
                onClick={clearLogo}
                data-testid="business-logo-clear-btn"
                className="h-10 px-4 rounded-lg bg-white border border-rose-300 text-rose-600 hover:bg-rose-50 font-bold text-sm flex items-center gap-2 disabled:opacity-40"
              >
                <Trash2 size={16} /> Αφαίρεση
              </button>
            )}
          </div>
          {lastError && (
            <div className="flex items-start gap-2 text-rose-700 text-xs bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mt-2">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <span>{lastError}</span>
            </div>
          )}
        </div>
      </div>

      {/* Print ON / OFF toggles — one for the fiscal receipt, one for
          the non-fiscal Λογαριασμός/Delivery slip. Kept independent so
          the operator can keep the customer's bill clean or print the
          logo only on receipts for marketing. */}
      <label
        className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer select-none transition-all ${
          printEnabled
            ? "border-emerald-400 bg-emerald-50"
            : "border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        <div className="flex items-center gap-3">
          <Printer
            size={20}
            className={printEnabled ? "text-emerald-600" : "text-slate-400"}
          />
          <div>
            <div className="font-bold text-sm">
              Εκτύπωση Logo στις Αποδείξεις
            </div>
            <div className="text-[11px] text-slate-500">
              Όταν ενεργό, το logo τυπώνεται στο header κάθε φορολογικής απόδειξης
            </div>
          </div>
        </div>
        <input
          type="checkbox"
          checked={!!printEnabled}
          onChange={(e) => togglePrint(e.target.checked)}
          disabled={!value}
          data-testid="business-logo-print-toggle"
          className="w-5 h-5 accent-emerald-600 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
        />
      </label>

      <label
        className={`mt-2 flex items-center justify-between gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer select-none transition-all ${
          printProformaEnabled
            ? "border-emerald-400 bg-emerald-50"
            : "border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        <div className="flex items-center gap-3">
          <Printer
            size={20}
            className={printProformaEnabled ? "text-emerald-600" : "text-slate-400"}
          />
          <div>
            <div className="font-bold text-sm">
              Εκτύπωση Logo στον Λογαριασμό & Delivery
            </div>
            <div className="text-[11px] text-slate-500">
              Όταν ενεργό, το logo τυπώνεται και στο πρόχειρο Λογαριασμό και στο Delivery slip
            </div>
          </div>
        </div>
        <input
          type="checkbox"
          checked={!!printProformaEnabled}
          onChange={(e) => togglePrintProforma(e.target.checked)}
          disabled={!value}
          data-testid="business-logo-print-proforma-toggle"
          className="w-5 h-5 accent-emerald-600 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
        />
      </label>
      {!value && printEnabled !== undefined && (
        <p className="text-[11px] text-slate-500 mt-1.5 ml-1">
          Ανέβασε πρώτα logo για να ενεργοποιήσεις την εκτύπωση.
        </p>
      )}

      {/* Helper note */}
      <details className="mt-4 text-xs">
        <summary className="cursor-pointer text-slate-600 font-bold hover:text-slate-900">
          Πώς να φτιάξω το BMP;
        </summary>
        <ol className="mt-2 ml-5 list-decimal space-y-1 text-slate-600">
          <li>Άνοιξε την εικόνα σου σε ένα editor (π.χ. <strong>Paint</strong> ή Photoshop)</li>
          <li>Resize σε <strong>570 × 300 pixels</strong> ακριβώς</li>
          <li>Μετατροπή σε <strong>Black &amp; White</strong> (1-bit)</li>
          <li>Save As → τύπος αρχείου: <strong>«Monochrome Bitmap (*.bmp)»</strong></li>
          <li>Επιβεβαίωσε ότι το αρχείο είναι κάτω από 500 KB (συνήθως μόλις ~20 KB σε αυτό το format)</li>
        </ol>
      </details>
    </div>
  );
}
